<template>
  <div>
    <!-- Price & Key Info Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div v-if="deal.price">
        <h3 class="text-sm text-gray-600 mb-2">{{ t('property.price') }}</h3>
        <div class="text-3xl font-bold text-brand">
          {{ formatPrice(deal.price) }}
        </div>
        <div v-if="deal.managementFee" class="text-sm text-gray-600 mt-1">
          {{ t('property.management_fee') }}: {{ formatPrice(deal.managementFee) }} / {{ t('property.month') }}
        </div>
        <div v-if="deal.repairReserveFund" class="text-sm text-gray-600">
          {{ t('property.repair_reserves') }}: {{ formatPrice(deal.repairReserveFund) }} / {{ t('property.month') }}
        </div>
      </div>

      <div v-if="deal.rent">
        <h3 class="text-sm text-gray-600 mb-2">{{ t('property.rent') }}</h3>
        <div class="text-3xl font-bold text-brand">
          {{ formatPrice(deal.rent) }} / {{ t('property.month') }}
        </div>
        <div v-if="deal.managementFee" class="text-sm text-gray-600 mt-1">
          {{ t('property.management_fee') }}: {{ formatPrice(deal.managementFee) }} / {{ t('property.month') }}
        </div>
      </div>

      <div>
        <h3 class="text-sm text-gray-600 mb-2">{{ t('property.layout_and_size') }}</h3>
        <div class="text-2xl font-bold text-gray-900">
          <template v-if="locale === 'ja' && (deal.layout?.type || deal.floorPlan)">
            {{ deal.layout?.type ? ((deal.layout.roomCount ?? '') + deal.layout.type) : deal.floorPlan }} /
          </template>
          <template v-else>
            <template v-if="deal.layout?.roomCount">{{ deal.layout.roomCount }} rooms / </template>
            <template v-if="deal.layout?.type">{{ deal.layout.type }} / </template>
          </template>
          <span v-if="size">{{ formatArea(size) }}</span>
        </div>
      </div>
    </div>

    <!-- Property Details Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-6 border-t border-gray-200">
      <div v-for="block in infoBlocks" :key="block.name" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
        <component :is="block.icon" class="size-5 text-gray-600" />
        <div>
          <div class="text-xs text-gray-600">{{ t('property.' + block.name) }}</div>
          <div class="font-semibold" :class="{capitalize: block.capitalize}">{{ block.value }}</div>
        </div>
      </div>
    </div>

    <!-- Last Updated Note -->
    <div v-if="formattedUpdatedAt" class="text-sm text-gray-500 mt-6">
      {{ t('property.last_updated') }}: {{ formattedUpdatedAt }}
    </div>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency, useAreaUnits, usePropertyDetails } from '@repo/ui';
import type { Deal, LocaleCode } from '@repo/types';
import type { ExchangeRates } from '@repo/modules';

const props = defineProps<{
  deal: Deal;
  currencies: ExchangeRates;
}>();

const { t, locale } = useI18n();
const { format: formatPrice } = useCurrency(props.currencies);
const { format: formatArea } = useAreaUnits();
const { infoBlocks } = usePropertyDetails(computed(() => props.deal), props.currencies, locale as Ref<LocaleCode>);

const size = computed(() =>
  props.deal.building.type == 'land' ? props.deal.land?.sqm : props.deal.sqm ??
  props.deal.building.sqm);

const formattedUpdatedAt = computed(() => {
  if (props.deal.updatedAt) {
    const date = new Date(props.deal.index.updatedAt || props.deal.updatedAt);
    return new Intl.DateTimeFormat(locale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  }
  return null;
});
</script>
